import { GameFrame, type GameFrameHandle } from '@mogzo/play';
import type { LaunchParams } from '@mogzo/shared';
import { ChevronLeft } from 'lucide-react';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMatchChat } from '../../lib/match-chat';
import { ChatDock, useCanChat } from '../chat/ChatDock';
import { ReportDialog } from '../chat/ReportDialog';
import { TopBar } from '../frame/TopBar';
import { useScreenSize } from './launch';
import { MatchChatSide } from './MatchChatSide';

export const pillClass =
  'inline-flex min-h-11 items-center gap-1.5 rounded-full border border-line bg-card px-5 text-[15px] font-semibold hover:border-muted';

export function BarButton({
  label,
  onClick,
  pressed,
  children,
}: {
  label: string;
  onClick: () => void;
  /** For a switch: whether it's on. */
  pressed?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      className="flex h-8 w-10 items-center justify-center rounded-lg text-white hover:bg-white/15"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {children}
      </svg>
    </button>
  );
}

/** Tailwind's `lg`: the game and its posts side by side, filling the window. */
const WIDE_QUERY = '(min-width: 1024px)';
const isWide = () => window.matchMedia?.(WIDE_QUERY).matches ?? false;
function watchWide(onChange: () => void) {
  const query = window.matchMedia?.(WIDE_QUERY);
  query?.addEventListener?.('change', onChange);
  return () => query?.removeEventListener?.('change', onChange);
}

/** Whether `element` is the page's full-screen element. */
function useFullScreen(element: RefObject<HTMLElement | null>) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    const onChange = () =>
      setFull(Boolean(element.current) && document.fullscreenElement === element.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [element]);
  return full;
}

/** A yes/no setting kept in this browser, when it can be. */
function useStoredSwitch(key: string, initial: boolean) {
  const [on, setOn] = useState(() => {
    try {
      const saved = localStorage.getItem(key);
      return saved === null ? initial : saved === '1';
    } catch {
      return initial;
    }
  });
  const toggle = useCallback(() => {
    setOn((was) => {
      try {
        localStorage.setItem(key, was ? '0' : '1');
      } catch {
        // Storage blocked: the switch still works for this visit.
      }
      return !was;
    });
  }, [key]);
  return [on, toggle] as const;
}

/** The control bar's height, the gap under the player and the posts panel's narrowest width. */
const BAR = 44;
const GAP = 16;
const ROW_GAP = 24;
const PANEL_MIN = 380;
/** Room for the game's name and buttons under a portrait game. */
const MAIN_MIN = 560;

interface Fit {
  /** The column under the game: the game's width, or MAIN_MIN for a narrow portrait game. */
  main: number;
  width: number;
  height: number;
}

/**
 * On a wide screen, the game's size: as tall as the window allows once what's under it is
 * placed, in its own shape, so the player is exactly the game with no black at its sides. The
 * width left over goes to the posts.
 */
function useFit(
  enabled: boolean,
  portrait: boolean,
  row: RefObject<HTMLDivElement | null>,
  below: RefObject<HTMLDivElement | null>,
) {
  const [fit, setFit] = useState<Fit | null>(null);

  useLayoutEffect(() => {
    const rowEl = row.current;
    const belowEl = below.current;
    if (!enabled || !rowEl || !belowEl) {
      setFit(null);
      return;
    }
    const measure = () => {
      const style = getComputedStyle(rowEl);
      const innerW =
        rowEl.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const innerH =
        rowEl.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      const maxMain = innerW - PANEL_MIN - ROW_GAP;
      const maxHeight = innerH - belowEl.offsetHeight - GAP - BAR;
      if (maxMain <= 0 || maxHeight <= 0) return setFit(null);
      let width: number;
      let height: number;
      if (portrait) {
        height = maxHeight;
        width = Math.min((height * 9) / 16, maxMain);
        height = (width * 16) / 9;
      } else {
        width = Math.min((maxHeight * 16) / 9, maxMain);
        height = (width * 9) / 16;
      }
      const next = {
        main: Math.floor(Math.min(maxMain, Math.max(width, MAIN_MIN))),
        width: Math.floor(width),
        height: Math.floor(height),
      };
      setFit((was) =>
        was && was.main === next.main && was.width === next.width && was.height === next.height
          ? was
          : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(rowEl);
    observer.observe(belowEl);
    return () => observer.disconnect();
  }, [enabled, portrait, row, below]);

  return enabled ? fit : null;
}

/**
 * A game's play page on a computer, live or in a test (M29): mogzo.com's header (M33) and,
 * under it, only the game, what goes under it (`children`) and its posts beside it (`side`,
 * given the Back button); no sidebar. On a wide screen the page fills the window and never scrolls; only the posts do.
 * Phones and tablets play full screen instead.
 */
export function PlayScreen({
  name,
  playUrl,
  projectId,
  portrait,
  launch,
  invite = null,
  openMatch = null,
  isDeveloper,
  home,
  children,
  side,
}: {
  name: string;
  playUrl: string;
  projectId: string;
  portrait: boolean;
  launch: LaunchParams;
  /** An invite code from a match link (mogzo.com/match/<code>), for the game to join with. */
  invite?: string | null;
  /** A match to open, from a notification or email (`?open=`, M33). */
  openMatch?: string | null;
  isDeveloper: boolean;
  /** Where Back goes when the player opened this address directly. */
  home: string;
  children: ReactNode;
  /** The posts beside the game; `back` goes at their top on a wide screen, else it's null. */
  side: (back: ReactNode) => ReactNode;
}) {
  const row = useRef<HTMLDivElement>(null);
  const below = useRef<HTMLDivElement>(null);
  const player = useRef<HTMLElement>(null);
  const game = useRef<GameFrameHandle>(null);
  const navigate = useNavigate();
  const { key } = useLocation();
  const size = useScreenSize();
  const wide = useSyncExternalStore(watchWide, isWide);
  const full = useFullScreen(player);
  const fit = useFit(wide && !full, portrait, row, below);
  // Chat in the game's match (M38b): beside the game in the panel look, over it in the compact.
  const canChat = useCanChat();
  const [sideTab, setSideTab] = useState<'posts' | 'chat'>('posts');
  const showChatTab = useCallback(() => setSideTab('chat'), []);
  const matchChat = useMatchChat(game, {
    enabled: canChat,
    openPanel: wide ? showChatTab : undefined,
  });
  const chatBeside = wide && matchChat.view?.look === 'panel' ? matchChat.view : null;

  // Esc opens MOGZO's pause menu while the page (not the game) has the keyboard; a panel's own
  // Esc closes it instead. In full screen the browser takes Esc and leaves full screen.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || document.fullscreenElement) return;
      game.current?.openPanel({ kind: 'pause' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // In full screen the bar can hide, and come back when the pointer reaches the bottom edge.
  const [autoHide, toggleAutoHide] = useStoredSwitch('mogzo.play.autoHide', true);
  const floating = full && autoHide;
  const [barShown, setBarShown] = useState(true);
  const hideTimer = useRef<number | undefined>(undefined);
  const hideBarSoon = useCallback((ms: number) => {
    setBarShown(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => setBarShown(false), ms);
  }, []);
  const keepBar = () => {
    setBarShown(true);
    window.clearTimeout(hideTimer.current);
  };
  useEffect(() => {
    if (floating) hideBarSoon(2500);
    else {
      window.clearTimeout(hideTimer.current);
      setBarShown(true);
    }
  }, [floating, hideBarSoon]);
  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  // Back where the player came from; `home` when they opened this address directly.
  const back = () => (key === 'default' ? navigate(home) : navigate(-1));
  const backButton = (
    <button
      type="button"
      onClick={back}
      className="inline-flex min-h-10 items-center gap-1 rounded-full border border-line bg-card pr-4 pl-2.5 text-sm font-semibold hover:border-muted"
    >
      <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
      Back
    </button>
  );

  return (
    <div className="bg-canvas lg:flex lg:h-dvh lg:flex-col">
      <TopBar />
      <div
        ref={row}
        className="flex min-h-dvh flex-col gap-4 bg-canvas p-3 text-ink sm:p-5 lg:min-h-0 lg:flex-1 lg:flex-row lg:justify-center lg:gap-6 lg:overflow-hidden"
      >
        <main
          style={fit ? { width: fit.main, flex: 'none' } : undefined}
          className="flex min-w-0 flex-col gap-4 lg:min-h-0 lg:flex-[999_1_0%]"
        >
          {!wide && <div className="flex shrink-0">{backButton}</div>}

          <section
            ref={player}
            aria-label={name}
            style={fit ? { width: fit.width, flex: 'none' } : undefined}
            onMouseMove={(event) => {
              if (floating && event.clientY > window.innerHeight - 96) hideBarSoon(2000);
            }}
            className="relative flex shrink-0 flex-col self-center overflow-hidden rounded-2xl bg-black lg:min-h-0 lg:flex-1 [&:not(:fullscreen)]:w-full"
          >
            <div
              style={fit ? { height: fit.height, flex: 'none' } : undefined}
              className={`relative [container-type:size] ${portrait ? 'h-[75dvh]' : 'aspect-video'} lg:aspect-auto lg:h-auto lg:min-h-0 lg:flex-1 [:fullscreen_&]:aspect-auto [:fullscreen_&]:h-auto [:fullscreen_&]:min-h-0 [:fullscreen_&]:flex-1`}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="relative"
                  style={
                    portrait
                      ? { height: 'min(100cqh, 177.78cqw)', aspectRatio: '9 / 16' }
                      : { width: 'min(100cqw, 177.78cqh)', aspectRatio: '16 / 9' }
                  }
                >
                  <GameFrame
                    ref={game}
                    url={playUrl}
                    title={name}
                    projectId={projectId}
                    launch={launch}
                    invite={invite}
                    openMatch={openMatch}
                    isDeveloper={isDeveloper}
                    panels={{ device: size, gameName: name, onExit: back }}
                    chat={matchChat.frameChat}
                  />
                </div>
              </div>
            </div>
            {/* The game takes the pointer, so a thin edge at the bottom brings the bar back. */}
            {floating && !barShown && (
              <div
                aria-hidden="true"
                onMouseEnter={keepBar}
                className="absolute inset-x-0 bottom-0 z-20 h-3"
              />
            )}
            <div
              role="toolbar"
              aria-label="Game controls"
              onMouseEnter={floating ? keepBar : undefined}
              onMouseLeave={floating ? () => hideBarSoon(1500) : undefined}
              className={`flex h-11 shrink-0 items-center justify-between bg-black px-1.5 ${floating ? `absolute inset-x-0 bottom-0 z-30 bg-black/85 transition duration-200 focus-within:translate-y-0 focus-within:opacity-100 ${barShown ? '' : 'pointer-events-none translate-y-full opacity-0'}` : ''}`}
            >
              <div className="flex items-center gap-0.5">
                <BarButton label="Pause" onClick={() => game.current?.openPanel({ kind: 'pause' })}>
                  <rect
                    x="6"
                    y="5"
                    width="4"
                    height="14"
                    rx="1"
                    fill="currentColor"
                    stroke="none"
                  />
                  <rect
                    x="14"
                    y="5"
                    width="4"
                    height="14"
                    rx="1"
                    fill="currentColor"
                    stroke="none"
                  />
                </BarButton>
                <BarButton label="Restart" onClick={() => game.current?.restart()}>
                  <path d="M4 12a8 8 0 1 0 2.4-5.7M4 4v4h4" />
                </BarButton>
              </div>
              <div className="flex items-center gap-0.5">
                {full && (
                  <BarButton
                    label={autoHide ? 'Auto-hide controls: on' : 'Auto-hide controls: off'}
                    pressed={autoHide}
                    onClick={toggleAutoHide}
                  >
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <path d="M3 15h18" />
                    <path d={autoHide ? 'M9 8l3 3 3-3' : 'M9 11l3-3 3 3'} />
                  </BarButton>
                )}
                {full ? (
                  <BarButton
                    label="Exit full screen"
                    onClick={() => document.exitFullscreen?.().catch(() => undefined)}
                  >
                    <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
                  </BarButton>
                ) : (
                  <BarButton
                    label="Full screen"
                    onClick={() => player.current?.requestFullscreen?.().catch(() => undefined)}
                  >
                    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
                  </BarButton>
                )}
              </div>
            </div>
          </section>

          <div ref={below} className="flex shrink-0 flex-col gap-4">
            {children}
          </div>
        </main>

        {chatBeside ? (
          <div className="flex min-h-0 flex-col gap-3 lg:max-w-[560px] lg:min-w-[380px] lg:flex-1">
            <div
              role="tablist"
              aria-label="Beside the game"
              className="grid shrink-0 grid-cols-2 rounded-full bg-field p-1"
            >
              {(['posts', 'chat'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={sideTab === tab}
                  onClick={() => setSideTab(tab)}
                  className={`flex h-9 items-center justify-center rounded-full text-sm font-semibold ${sideTab === tab ? 'bg-card text-ink shadow-[0_1px_2px_rgba(27,21,47,0.1)]' : 'text-muted hover:text-ink'}`}
                >
                  {tab === 'posts' ? 'Posts' : 'Match chat'}
                </button>
              ))}
            </div>
            {sideTab === 'posts' ? (
              side(backButton)
            ) : (
              <aside
                aria-label="Match chat"
                className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-line bg-card"
              >
                <MatchChatSide
                  view={chatBeside}
                  send={matchChat.send}
                  onReport={matchChat.setReporting}
                />
              </aside>
            )}
          </div>
        ) : (
          side(wide ? backButton : null)
        )}
      </div>
      {canChat && <ChatDock />}
      {matchChat.reporting && (
        <ReportDialog message={matchChat.reporting} onClose={() => matchChat.setReporting(null)} />
      )}
    </div>
  );
}
